import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import {
	getRestaurantBySlug,
	getMenuItems,
	addOrder,
} from "../services/firebaseService";
import {
	trackAddToCart,
	trackPlaceOrder,
	trackPageView,
} from "../services/analyticsService"; // Import analytics functions
import { getCurrencySymbol } from "../config/currencies";
import { FaCheckCircle } from "react-icons/fa"; // Import success icon
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./MenuPage.css";
import LoadingSpinner from "../components/loadingspinner";
import { db } from "../firebase";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { applyPriceAdjustment, getDisplayPrice } from "../utils/priceHelper";

function TableBookingPage() {
	const { slug } = useParams();
	const navigate = useNavigate();
	const [restaurant, setRestaurant] = useState(null);
	const [menuItems, setMenuItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [cart, setCart] = useState([]);
	const [activeCategory, setActiveCategory] = useState("All");
	const [searchTerm, setSearchTerm] = useState("");
	const [categories, setCategories] = useState(["All"]);
	const [showImageModal, setShowImageModal] = useState(false);
	const [modalItem, setModalItem] = useState(null);
	const [isBooking, setIsBooking] = useState(false);
	const [successMsg, setSuccessMsg] = useState("");
	const [showInfoDialog, setShowInfoDialog] = useState(true); // Dialog visibility
	const [customerName, setCustomerName] = useState("");
	const [selectedPortions, setSelectedPortions] = useState({});
	const [tableNumber, setTableNumber] = useState("");
	const [showSuccessDialog, setShowSuccessDialog] = useState(false);
	const [lastOrderDetails, setLastOrderDetails] = useState(null);
    // New state for Seat Areas
    const [seatAreas, setSeatAreas] = useState([]);
    const [selectedSeatAreaId, setSelectedSeatAreaId] = useState("");

	const cartRef = useRef(null);
	const containerRef = useRef(null);
	const location = useLocation(); // Add this line

	useEffect(() => {
		// Track page view when component mounts and slug is available
		if (slug) {
			trackPageView(
				`TableBooking - ${slug}`,
				location.pathname + location.search,
				restaurant?.id
			);
		}
	}, [slug, location.pathname, location.search]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const tableNumFromQuery = params.get("table");
        const seatCategoryFromQuery = params.get("area");

		if (tableNumFromQuery) {
			setTableNumber(tableNumFromQuery);
			setCustomerName("Guest"); // Set a default name
			setShowInfoDialog(false); // Skip the dialog
		}

        if (seatCategoryFromQuery && seatAreas.some(c => c.id === seatCategoryFromQuery)) {
            setSelectedSeatAreaId(seatCategoryFromQuery);
        } else if (seatAreas.length > 0) {
            setSelectedSeatAreaId(seatAreas[0].id);
        }
	}, [location.search, seatAreas]); // Re-run if search params change or categories load

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const restaurantData = await getRestaurantBySlug(slug);
				if (!restaurantData) {
					setError("Restaurant not found");
					setLoading(false);
					return;
				}
				if (!restaurantData) {
					setError("Restaurant not found");
					setLoading(false);
					return;
				}
				setRestaurant(restaurantData);
                setSeatAreas(Array.isArray(restaurantData.seatCategories) ? restaurantData.seatCategories : []);
                
                const params = new URLSearchParams(window.location.search);
                const seatCategoryFromQuery = params.get("area");

                // Default to the first category if available, or use the one from URL
                if (seatCategoryFromQuery && Array.isArray(restaurantData.seatCategories) && restaurantData.seatCategories.some(c => c.id === seatCategoryFromQuery)) {
                    setSelectedSeatAreaId(seatCategoryFromQuery);
                } else if (Array.isArray(restaurantData.seatCategories) && restaurantData.seatCategories.length > 0) {
                    setSelectedSeatAreaId(restaurantData.seatCategories[0].id);
                }


				// Check if the restaurant is active
				if (restaurantData.isActive === false) {
					// Explicitly check for false
					setError("This restaurant is currently unavailable.");
					setLoading(false);
					return;
				}

				// Check if the restaurant is closed
				if (restaurantData.isClosed === true) {
					setError("This restaurant is currently closed.");
					setLoading(false);
					return;
				}

				// Check if table booking is enabled
				if (restaurantData.tableBookingEnabled === false) {
					// Explicitly check if table booking is disabled
					setError(
						"Table booking is currently disabled for this restaurant."
					);
					setLoading(false);
					return;
				}

				// First, check for active menus in the new menu structure
				let items = [];

				try {
					console.log(
						`TableBookingPage: Checking for active menus for restaurant ${restaurantData.id}`
					);
					const menusRef = collection(
						db,
						"restaurants",
						restaurantData.id,
						"menus"
					);
					const activeMenuQuery = query(
						menusRef,
						where("isActive", "==", true),
						orderBy("createdAt", "desc")
					);
					const activeMenuSnap = await getDocs(activeMenuQuery);

					console.log(
						`TableBookingPage: Found ${activeMenuSnap.size} active menus`
					);

					if (!activeMenuSnap.empty) {
						// If there are active menus, fetch items from all of them
						console.log(
							`TableBookingPage: Using ${activeMenuSnap.size} active menus`
						);

						for (const activeMenuDoc of activeMenuSnap.docs) {
							const activeMenu = activeMenuDoc;
							console.log(
								`TableBookingPage: Fetching items from menu: ${
									activeMenu.data().name
								} (ID: ${activeMenu.id})`
							);

							const menuItemsRef = collection(
								db,
								"restaurants",
								restaurantData.id,
								"menus",
								activeMenu.id,
								"menuItems"
							);
							const menuItemsSnap = await getDocs(menuItemsRef);

							const menuItems = menuItemsSnap.docs.map((doc) => ({
								id: doc.id,
								menuId: activeMenu.id,
								menuName: activeMenu.data().name,
								...doc.data(),
							}));

							items = items.concat(menuItems);
							console.log(
								`TableBookingPage: Loaded ${
									menuItems.length
								} items from menu: ${activeMenu.data().name}`
							);
						}

						console.log(
							`TableBookingPage: Total loaded ${items.length} items from all active menus`
						);
					} else {
						// Fallback to legacy menu items if no active menu exists
						console.log(
							`TableBookingPage: No active menus found, falling back to legacy menu`
						);
						items = await getMenuItems(restaurantData.id);
						console.log(
							`TableBookingPage: Loaded ${items.length} items from legacy menu`
						);
					}
				} catch (menuError) {
					console.error(
						"TableBookingPage: Error fetching active menus, falling back to legacy:",
						menuError
					);
					// If there's any error with the new menu structure, fall back to legacy
					try {
						items = await getMenuItems(restaurantData.id);
						console.log(
							`TableBookingPage: Fallback successful - loaded ${items.length} items from legacy menu`
						);
					} catch (legacyError) {
						console.error(
							"TableBookingPage: Error fetching legacy menu items:",
							legacyError
						);
						throw legacyError; // Re-throw if even legacy fails
					}
				}

				// Include all items, don't filter out hidden ones
				setMenuItems(items);

				// Initialize selectedPortions for portion-wise items
				const initialPortions = {};
				items.forEach((item) => {
					if (
						item.isPortionWise &&
						item.portions &&
						item.portions.length > 0
					) {
						initialPortions[item.id] = item.portions[0].name; // Default to first portion
					}
				});
				setSelectedPortions(initialPortions);

				const uniqueCategories = [
					...new Set(items.map((item) => item.category)),
				];
				setCategories(["All", ...uniqueCategories]);
				setLoading(false);
			} catch (error) {
				console.error("Error fetching data:", error); // Log the actual error
				setError("Failed to load menu data. " + error.message);
				setLoading(false);
			}
		};
		fetchData();
	}, [slug]);

	useEffect(() => {
		if (cartRef.current && containerRef.current) {
			const cartHeight = cartRef.current.offsetHeight;
			// Add some extra padding, e.g., 20px, to ensure content is not cut off
			const newPaddingBottom = cartHeight > 0 ? cartHeight + 20 : 200;
			containerRef.current.style.paddingBottom = `${newPaddingBottom}px`;
		}
	}, [cart, loading]); // Re-calculate when cart changes or after loading completes

    // Helper to get current pricing adjustment based on selected category
    const getCurrentPricingAdjustment = () => {
        if (!selectedSeatAreaId) return null;
        return seatAreas.find(c => c.id === selectedSeatAreaId) || null;
    };

    // Effect to recalculate cart prices when seat category changes
    useEffect(() => {
        if (cart.length > 0) {
            const adjustment = getCurrentPricingAdjustment();
            setCart(prevCart => prevCart.map(item => ({
                ...item,
                price: applyPriceAdjustment(item.baseEffectivePrice, restaurant, "table", adjustment),
                originalPrice: applyPriceAdjustment(item.baseOriginalPrice, restaurant, "table", adjustment)
            })));
        }
    }, [selectedSeatAreaId, restaurant]);

	const handleImageClick = (item) => {
		setModalItem(item);
		setShowImageModal(true);
	};
	const closeModal = () => {
		setShowImageModal(false);
		setModalItem(null);
	};

	const filteredItems = menuItems.filter((item) => {
		const matchesCategory =
			activeCategory === "All" || item.category === activeCategory;
		const matchesSearch =
			searchTerm === "" ||
			item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			(item.description &&
				item.description
					.toLowerCase()
					.includes(searchTerm.toLowerCase()));
		return matchesCategory && matchesSearch;
	});

	// Helper function to handle portion selection change
	const handlePortionChange = (itemId, portion) => {
		setSelectedPortions((prev) => ({
			...prev,
			[itemId]: portion,
		}));
	};

	const addToCart = (item) => {
		setCart((prev) => {
			// For portion-wise items, create a unique identifier that includes the portion
			const selectedPortion =
				item.isPortionWise && item.portions && item.portions.length > 0
					? selectedPortions[item.id] || item.portions[0].name
					: null;
			const cartItemId = item.isPortionWise
				? `${item.id}_${selectedPortion}`
				: item.id;

			const found = prev.find((ci) => ci.cartItemId === cartItemId);
			if (found) {
				return prev.map((ci) =>
					ci.cartItemId === cartItemId
						? { ...ci, qty: ci.qty + 1 }
						: ci
				);
			}

			const baseOriginal = item.isPortionWise
				? (function () {
						const selectedPortion =
							item.isPortionWise &&
							item.portions &&
							item.portions.length > 0
								? selectedPortions[item.id] ||
								  item.portions[0].name
								: null;
						const portion =
							item.portions &&
							item.portions.find(
								(p) => p.name === selectedPortion
							);
						const raw = portion ? portion.price : item.price;
						const num =
							typeof raw === "number" ? raw : parseFloat(raw);
						return isFinite(num) ? num : 0;
				  })()
				: (function () {
						const raw = item.price;
						const num =
							typeof raw === "number" ? raw : parseFloat(raw);
						return isFinite(num) ? num : 0;
				  })();
			const baseEffective = item.isPortionWise
				? baseOriginal
				: (function () {
						const rawOffer = item.offerPrice;
						const rawPrice = item.price;
						const offerNum =
							typeof rawOffer === "number"
								? rawOffer
								: parseFloat(rawOffer);
						const priceNum =
							typeof rawPrice === "number"
								? rawPrice
								: parseFloat(rawPrice);
						const hasOffer =
							isFinite(offerNum) &&
							isFinite(priceNum) &&
							offerNum < priceNum;
						return hasOffer
							? offerNum
							: isFinite(priceNum)
							? priceNum
							: 0;
				  })();

            const adjustment = getCurrentPricingAdjustment();
			const cartItem = {
				...item,
				cartItemId: cartItemId,
				qty: 1,
                baseEffectivePrice: baseEffective, // Store raw effective price for recalculation
                baseOriginalPrice: baseOriginal,   // Store raw original price for recalculation
				price: applyPriceAdjustment(baseEffective, restaurant, "table", adjustment),
				originalPrice: applyPriceAdjustment(
					baseOriginal,
					restaurant,
					"table",
                    adjustment
				),
				name: item.isPortionWise
					? `${item.name} (${selectedPortion})`
					: item.name,
				selectedPortion: item.isPortionWise ? selectedPortion : null,
			};

			// New item added to cart
			trackAddToCart(cartItem, restaurant?.id); // Track add to cart event with restaurantId
			return [...prev, cartItem];
		});
	};

	const removeFromCart = (cartItemId) => {
		setCart((prev) => prev.filter((ci) => ci.cartItemId !== cartItemId));
	};

	const updateQty = (cartItemId, delta) => {
		setCart((prev) =>
			prev.map((ci) =>
				ci.cartItemId === cartItemId
					? { ...ci, qty: Math.max(1, ci.qty + delta) }
					: ci
			)
		);
	};

	const handleBook = async () => {
		if (!restaurant || cart.length === 0) return;
		if (!customerName || !tableNumber) {
			setError(
				"Please enter your name and table number before confirming the order."
			);
			setShowInfoDialog(true); // Re-open dialog if not filled
			return;
		}
		setIsBooking(true);
		setError("");
		setSuccessMsg(""); // Clear previous messages
		try {
			const orderPayload = {
				customerName: customerName, // Use state variable
				tableNumber: tableNumber, // Use state variable
				items: cart.map((ci) => ({
					menuItemId: ci.id, // Storing menuItemId for better tracking
					name: ci.name,
					qty: ci.qty,
					price: ci.price, // Use the portion-wise price from cart item
					menuItemId: ci.id, // Storing menuItemId for better tracking
					name: ci.name,
					qty: ci.qty,
					price: ci.price, // Use the portion-wise price from cart item
					originalPrice: ci.originalPrice, // Storing original price for reference
				})),
                seatArea: selectedSeatAreaId ? (seatAreas.find(c => c.id === selectedSeatAreaId)?.name || 'Unknown') : 'Standard',
				totalAmount: cart.reduce(
					(sum, ci) => sum + ci.price * ci.qty,
					0
				),
				status: "new", // Or 'received', 'pending_confirmation'
				createdAt: new Date(), // Add timestamp for the order
			};

			// Ensure restaurant.id is available
			if (!restaurant.id) {
				console.error(
					"Restaurant ID is missing in handleBook, cannot place order."
				);
				setError(
					"Error: Restaurant details are incomplete. Cannot place order."
				);
				setIsBooking(false);
				return;
			}

			// Pass restaurant.id as the first argument and the payload as the second
			// Assuming addOrder returns the order object with an ID or just the ID
			const newOrder = await addOrder(restaurant.id, orderPayload);

			// Track successful order placement
			trackPlaceOrder({
				restaurantId: restaurant.id,
				...orderPayload, // Spread the existing payload which includes totalAmount, items, customerName, tableNumber
			});

			// Store details for the success dialog
			setLastOrderDetails({
				...orderPayload,
				id: newOrder.id || "N/A", // Assuming newOrder has an id property or addOrder returns it
				orderNumber: newOrder.orderNumber, // Ensure this uses the orderNumber field from the service response
			});

			setCart([]); // Clear the cart
			setShowSuccessDialog(true); // Show success dialog
			setSuccessMsg(""); // Clear any previous redirect message

			// No longer redirecting immediately
			// setTimeout(() => {
			//   // Navigate to the orders page for this restaurant
			//   navigate(`/orders/${restaurant.id}`);
			// }, 2000); // Increased timeout for better UX
		} catch (err) {
			console.error("Failed to place order:", err);
			setError(
				`Failed to place order. ${
					err.message ? err.message : "Please try again."
				}`
			);
		} finally {
			setIsBooking(false);
		}
	};

	const currencySymbol =
		(restaurant &&
			restaurant.currency &&
			getCurrencySymbol(restaurant.currency)) ||
		"$";

	const handleOrderMore = () => {
		setShowSuccessDialog(false);
		setLastOrderDetails(null);
		// Optionally reset customer name and table number or show info dialog again
		// setCustomerName('');
		// setTableNumber('');
		// setCustomerName('');
		// setTableNumber('');
        // setSelectedSeatCategoryId(''); // Optional: reset seat category? Probably better to keep it.
		setShowInfoDialog(true); // Take user back to enter details for a new order
	};

	const handleDownloadReceipt = () => {
		if (!lastOrderDetails) return;

		const doc = new jsPDF();
		const currencyCode = restaurant?.currency || "USD"; // Use currency code, fallback to USD

		// Restaurant Name
		doc.setFontSize(18);
		doc.text(restaurant?.name || "Restaurant", 105, 20, {
			align: "center",
		});

		// Title
		doc.setFontSize(14);
		doc.text("Order Confirmation", 105, 30, { align: "center" });

		// Order Details
		doc.setFontSize(10);
		doc.text(`Order Number: ${lastOrderDetails.orderNumber}`, 14, 45);
		doc.text(`Customer Name: ${lastOrderDetails.customerName}`, 14, 52);
		doc.text(`Table Number: ${lastOrderDetails.tableNumber}`, 14, 59);
		doc.text(
			`Date: ${new Date(lastOrderDetails.createdAt).toLocaleString()}`,
			14,
			66
		);

		// Items Table
		const tableColumn = ["Item Name", "Quantity", "Price", "Total"];
		const tableRows = [];

		lastOrderDetails.items.forEach((item) => {
			const itemData = [
				item.name,
				item.qty,
				`${item.price.toFixed(2)} ${currencyCode}`,
				`${(item.price * item.qty).toFixed(2)} ${currencyCode}`,
			];
			tableRows.push(itemData);
		});

		autoTable(doc, {
			startY: 75,
			head: [tableColumn],
			body: tableRows,
			theme: "striped",
			headStyles: { fillColor: [22, 160, 133] }, // Green header
			margin: { top: 10 },
		});

		// Total Amount
		let finalY = doc.lastAutoTable.finalY || 75; // Get Y position after table
		doc.setFontSize(12);
		doc.setFont(undefined, "bold");
		doc.text(
			`Total Amount: ${lastOrderDetails.totalAmount.toFixed(
				2
			)} ${currencyCode}`,
			14,
			finalY + 15
		);

		// Thank you message
		doc.setFontSize(10);
		doc.setFont(undefined, "italic");
		doc.text("Thank you for your order!", 105, finalY + 25, {
			align: "center",
		});

		// Save PDF
		doc.save(`order_receipt_${lastOrderDetails.orderNumber}.pdf`);
	};

	const ImageModal = ({ item, onClose }) => {
		if (!item) return null;

		return (
			<div className="image-modal-overlay" onClick={onClose}>
				<div
					className="image-modal-content"
					onClick={(e) => e.stopPropagation()}
				>
					{item.photoUrl ? (
						<img src={item.photoUrl} alt={item.name} />
					) : (
						<div className="modal-image-placeholder">
							<i className="fas fa-utensils"></i>
							<span>No Image Available</span>
						</div>
					)}
					<div className="image-modal-info">
						<h3 className="modal-item-name">{item.name}</h3>
						{item.description && (
							<p className="modal-item-description">
								{item.description}
							</p>
						)}
						<div className="modal-item-price">
							{item.offerPrice && item.offerPrice < item.price ? (
								<>
									<span className="modal-offer-price">
										{getCurrencySymbol(
											restaurant?.currency
										)}
										{applyPriceAdjustment(
											item.offerPrice,
											restaurant,
											"table",
                                            getCurrentPricingAdjustment()
										).toFixed(2)}
									</span>
									<span className="modal-original-price">
										{getCurrencySymbol(
											restaurant?.currency
										)}
										{applyPriceAdjustment(
											item.price,
											restaurant,
											"table",
                                            getCurrentPricingAdjustment()
										).toFixed(2)}
									</span>
								</>
							) : (
								<span className="modal-regular-price">
									{getCurrencySymbol(restaurant?.currency)}
									{getDisplayPrice(
										item,
										selectedPortions,
										restaurant,
										"table",
                                        getCurrentPricingAdjustment()
									).toFixed(2)}
								</span>
							)}
						</div>
						{item.prepTime && (
							<div className="modal-prep-time">
								<i className="far fa-clock"></i>
								{item.prepTime} min
							</div>
						)}
					</div>
					<button className="image-modal-close" onClick={onClose}>
						&times;
					</button>
				</div>
			</div>
		);
	};

	if (loading) {
		return <LoadingSpinner />;
	}
	if (error) {
		// Check if it's a restaurant closure message
		const isClosedMessage = error.includes("closed");

		return (
			<div className="container centered">
				<div
					className={`error-container ${
						isClosedMessage ? "restaurant-closed" : ""
					}`}
				>
					{isClosedMessage ? (
						<>
							<div className="closed-icon">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 24 24"
									width="64"
									height="64"
									fill="#e74c3c"
								>
									<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z" />
								</svg>
							</div>
							<h2 className="closed-title">
								This Restaurant is Currently Closed
							</h2>
							<p className="closed-message">
								We're sorry, but this restaurant is not
								accepting table bookings at this time.
							</p>
							<p className="closed-hours">
								Please check back later during business hours.
							</p>
						</>
					) : (
						<p className="error-message">{error}</p>
					)}
					<Link to="/" className="btn mt-4">
						Go Home
					</Link>
				</div>
			</div>
		);
	}

	const handleInfoSubmit = (e) => {
		e.preventDefault();
		if (customerName && tableNumber) {
			setShowInfoDialog(false);
			setError(""); // Clear any previous error from dialog
		} else {
			setError("Name and Table Number are required.");
		}
	};

	if (
		showInfoDialog &&
		!showSuccessDialog &&
		!new URLSearchParams(location.search).get("table")
	) {
		// Ensure success dialog doesn't get hidden by info dialog and dialog shows if no table
		return (
			<div
				className="container centered"
				style={{
					padding: "20px",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				<form
					onSubmit={handleInfoSubmit}
					style={{
						background: "white",
						padding: "30px",
						borderRadius: "8px",
						boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
						width: "100%",
						maxWidth: "400px",
					}}
				>
					<h2 style={{ textAlign: "center", marginBottom: "20px" }}>
						Enter Your Details
					</h2>
					{error && (
						<p
							style={{
								color: "red",
								textAlign: "center",
								marginBottom: "15px",
							}}
						>
							{error}
						</p>
					)}
					<div style={{ marginBottom: "15px" }}>
						<label
							htmlFor="customerName"
							style={{
								display: "block",
								marginBottom: "5px",
								fontWeight: "bold",
							}}
						>
							Name:
						</label>
						<input
							type="text"
							id="customerName"
							value={customerName}
							onChange={(e) => setCustomerName(e.target.value)}
							required
							style={{
								width: "100%",
								padding: "10px",
								borderRadius: "5px",
								border: "1px solid #ccc",
							}}
						/>
					</div>
					<div style={{ marginBottom: "20px" }}>
						<label
							htmlFor="tableNumber"
							style={{
								display: "block",
								marginBottom: "5px",
								fontWeight: "bold",
							}}
						>
							Table Number:
						</label>
						<input
							type="text"
							id="tableNumber"
							value={tableNumber}
							onChange={(e) => setTableNumber(e.target.value)}
							required
							style={{
								width: "100%",
								padding: "10px",
								borderRadius: "5px",
								border: "1px solid #ccc",
							}}
						/>
					</div>
                    
                    {seatAreas.length > 1 && (
                        <div style={{ marginBottom: "20px" }}>
                            <label
                                htmlFor="seatArea"
                                style={{
                                    display: "block",
                                    marginBottom: "5px",
                                    fontWeight: "bold",
                                }}
                            >
                                Seating Area:
                            </label>
                            <select
                                id="seatArea"
                                value={selectedSeatAreaId}
                                onChange={(e) => setSelectedSeatAreaId(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    borderRadius: "5px",
                                    border: "1px solid #ccc",
                                }}
                            >
                                {seatAreas.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

					<button
						type="submit"
						className="btn btn-primary"
						style={{
							width: "100%",
							padding: "12px",
							fontSize: "16px",
						}}
					>
						Submit
					</button>
				</form>
			</div>
		);
	}

	if (showSuccessDialog && lastOrderDetails) {
		return (
			<div
				className="container centered"
				style={{
					padding: "20px",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				<div
					style={{
						background: "white",
						padding: "30px",
						borderRadius: "8px",
						boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
						width: "100%",
						maxWidth: "500px",
						textAlign: "center",
					}}
				>
					<FaCheckCircle
						size={50}
						color="green"
						style={{ marginBottom: "20px" }}
					/>
					<h2 style={{ marginBottom: "20px" }}>
						Order Placed Successfully!
					</h2>

					<div style={{ textAlign: "left", marginBottom: "20px" }}>
						<p>
							<strong>Order Number:</strong>{" "}
							{lastOrderDetails.orderNumber}
						</p>
						<p>
							<strong>Name:</strong>{" "}
							{lastOrderDetails.customerName}
						</p>
						<p>
							<strong>Table:</strong>{" "}
							{lastOrderDetails.tableNumber}
						</p>
						<h4 style={{ marginTop: "15px", marginBottom: "5px" }}>
							Items:
						</h4>
						<ul
							style={{
								listStyle: "none",
								paddingLeft: "0",
								maxHeight: "150px",
								overflowY: "auto",
							}}
						>
							{lastOrderDetails.items.map((item, index) => (
								<li key={index} style={{ marginBottom: "5px" }}>
									{item.name} (x{item.qty}) - {currencySymbol}
									{item.price.toFixed(2)}
								</li>
							))}
						</ul>
						<p style={{ marginTop: "10px", fontWeight: "bold" }}>
							<strong>Total:</strong> {currencySymbol}
							{lastOrderDetails.totalAmount.toFixed(2)}
						</p>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "space-around",
							marginTop: "30px",
						}}
					>
						<button
							onClick={handleOrderMore}
							className="btn btn-secondary"
							style={{ margin: "10px" }}
						>
							Order More
						</button>
						<button
							onClick={handleDownloadReceipt}
							className="btn btn-primary"
							style={{ margin: "10px" }}
						>
							Download Receipt
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className="container"
			ref={containerRef}
			style={{ paddingBottom: "200px" }}
		>
			<div
				className="header"
				style={{ position: "relative", width: "100%" }}
			>
				<div
					className="header-content"
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						width: "100%",
					}}
				>
					{restaurant?.logoUrl && (
						<div style={{ width: "100%" }}>
							<img
								src={restaurant.logoUrl}
								alt={`${restaurant.name} Logo`}
								style={{
									width: "100%",
									height: "auto",
									maxHeight: "150px",
									objectFit: "cover",
								}}
							/>
						</div>
					)}
					<h1
						style={{
							textAlign: "center",
							margin: "0",
							fontSize: "2rem",
							fontWeight: "bold",
							width: "100%",
						}}
					>
						{restaurant?.name}
					</h1>
				</div>
			</div>
			<div className="search-bar">
				<i className="fas fa-search"></i>
				<input
					type="text"
					placeholder="Search menu items..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
				/>
			</div>
			<div className="category-tabs">
				{categories.map((category) => (
					<div
						key={category}
						className={`category-tab ${
							activeCategory === category ? "active" : ""
						}`}
						onClick={() => setActiveCategory(category)}
					>
						{category}
					</div>
				))}
			</div>
			{filteredItems.length === 0 ? (
				<div className="centered mt-4">
					<p>No menu items found.</p>
				</div>
			) : (
				<div className="menu-grid">
					{filteredItems.map((item) => (
						<div
							key={item.id}
							className={`menu-item ${
								item.isHidden ? "menu-item-disabled" : ""
							}`}
						>
							{item.isHidden && (
								<div className="out-of-stock-overlay">
									<span>Out of Stock</span>
								</div>
							)}
							{item.photoUrl ? (
								<div
									className="menu-item-image-container"
									onClick={
										!item.isHidden
											? () => handleImageClick(item)
											: undefined
									}
									style={{
										cursor: item.isHidden
											? "default"
											: "pointer",
									}}
								>
									<img src={item.photoUrl} alt={item.name} />
								</div>
							) : (
								<div className="menu-item-placeholder"></div>
							)}
							{item.offerPrice &&
								item.offerPrice < item.price &&
								!item.isHidden && (
									<div className="offer-badge">Offer</div>
								)}
							<div
								className="menu-item-content"
								style={{ marginBottom: 10 }}
							>
								<div className="menu-item-category">
									<h3 className="menu-item-title">
										{item.name}
									</h3>
								</div>
								{item.description && (
									<p className="menu-item-description">
										{item.description}
									</p>
								)}

								{/* Portion Selection Radio Buttons - Only for portion-wise items */}
								{item.isPortionWise &&
									item.portions &&
									item.portions.length > 0 && (
										<div>
											<div
												style={{
													display: "flex",
													gap: "15px",
													marginBottom: "10px",
													justifyContent: "left",
													flexWrap: "wrap",
												}}
											>
												{item.portions.map(
													(portion) => (
														<label
															key={portion.name}
															style={{
																display: "flex",
																alignItems:
																	"center",
																cursor: "pointer",
																fontSize:
																	"0.9rem",
																fontWeight:
																	selectedPortions[
																		item.id
																	] ===
																	portion.name
																		? "bold"
																		: "normal",
																color:
																	selectedPortions[
																		item.id
																	] ===
																	portion.name
																		? "#2563eb"
																		: "#666",
															}}
														>
															<input
																type="radio"
																name={`portion-${item.id}`}
																value={
																	portion.name
																}
																checked={
																	selectedPortions[
																		item.id
																	] ===
																	portion.name
																}
																onChange={() =>
																	handlePortionChange(
																		item.id,
																		portion.name
																	)
																}
																style={{
																	marginRight:
																		"5px",
																}}
															/>
															{portion.name}
														</label>
													)
												)}
											</div>

											{/* Show description only for selected portion */}
											{(() => {
												const selectedPortion =
													selectedPortions[item.id] ||
													item.portions[0].name;
												const portion =
													item.portions.find(
														(p) =>
															p.name ===
															selectedPortion
													);
												return portion &&
													portion.description ? (
													<div
														style={{
															fontSize: "0.85rem",
															color: "#666",
															marginBottom:
																"10px",
															lineHeight: "1.3",
															fontStyle: "italic",
															padding: "8px 12px",
															backgroundColor:
																"#f8f9fa",
															borderRadius: "6px",
															border: "1px solid #e9ecef",
														}}
													>
														{portion.description}
													</div>
												) : null;
											})()}
										</div>
									)}

								<div className="menu-item-footer">
									<div className="menu-item-price">
										{item.offerPrice &&
										item.offerPrice < item.price &&
										!item.isPortionWise ? (
											<>
												{/* Offer price - displayed prominently */}
												<span
													style={{
														fontSize: "1rem", // Make offer price slightly larger
														fontWeight: "bold", // Make offer price bold
														color: "#2563eb", // Optional: make offer price blue to highlight savings
													}}
												>
													{getCurrencySymbol(
														restaurant?.currency
													)}
													{applyPriceAdjustment(
														item.offerPrice,
														restaurant,
														"table",
                                                        getCurrentPricingAdjustment()
													).toFixed(2)}
												</span>

												{/* Original price - displayed smaller with strikethrough */}
												<span
													style={{
														textDecoration:
															"line-through",
														color: "#888",
														marginLeft: "8px", // Changed to marginLeft for better spacing
														fontSize: "0.8rem", // Make original price smaller
													}}
												>
													{getCurrencySymbol(
														restaurant?.currency
													)}{" "}
													{applyPriceAdjustment(
														item.price,
														restaurant,
														"table",
                                                        getCurrentPricingAdjustment()
													).toFixed(2)}
												</span>
											</>
										) : (
											// If no offer price or portion-wise item, show appropriate price
											<span
												style={{ fontWeight: "bold" }}
											>
												{getCurrencySymbol(
													restaurant?.currency
												)}{" "}
												{getDisplayPrice(
													item,
													selectedPortions,
													restaurant,
													"table",
                                                    getCurrentPricingAdjustment()
												).toFixed(2)}
											</span>
										)}
									</div>
									<button
										className="btn add-btn"
										onClick={() => addToCart(item)}
										disabled={item.isHidden}
										style={{
											marginLeft: 10,
											opacity: item.isHidden ? 0.5 : 1,
											cursor: item.isHidden
												? "not-allowed"
												: "pointer",
										}}
									>
										Add
									</button>
								</div>
							</div>
						</div>
					))}
				</div>
			)}
			<div
				className="cart-section"
				ref={cartRef}
				style={{
					position: "fixed",
					bottom: 0,
					left: 0,
					right: 0,
					width: "100%",
					background: "#f9f9f9",
					padding: "20px",
					borderRadius: "8px 8px 0 0",
					zIndex: 1000,
					boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
				}}
			>
				<h2>Cart</h2>
				{cart.length === 0 ? (
					<p>Your cart is empty.</p>
				) : (
					<div>
						<div style={{ maxHeight: "200px", overflowY: "auto" }}>
							{cart.map((ci) => (
								<div
									key={ci.cartItemId}
									className="cart-item"
									style={{
										display: "flex",
										alignItems: "center",
										marginBottom: 10,
									}}
								>
									<span
										style={{
											flex: 1,
											whiteSpace: "break-spaces",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
									>
										{ci.name}
									</span>
									<span style={{ margin: "0 10px" }}>
										{currencySymbol}
										{ci.price}
									</span>
									<button
										onClick={() =>
											updateQty(ci.cartItemId, -1)
										}
										disabled={ci.qty === 1}
										style={{
											background: "none",
											border: "none",
											cursor: "pointer",
											padding: "5px",
											fontSize: "1.5em",
											color: "#007bff",
										}}
									>
										<i className="fas fa-minus-circle"></i>
									</button>
									<span
										style={{
											margin: "0 8px",
											verticalAlign: "middle",
											fontSize: "1.1em",
										}}
									>
										{ci.qty}
									</span>
									<button
										onClick={() =>
											updateQty(ci.cartItemId, 1)
										}
										style={{
											background: "none",
											border: "none",
											cursor: "pointer",
											padding: "5px",
											fontSize: "1.5em",
											color: "#28a745",
										}}
									>
										<i className="fas fa-plus-circle"></i>
									</button>
									<button
										onClick={() =>
											removeFromCart(ci.cartItemId)
										}
										style={{
											background: "none",
											border: "none",
											cursor: "pointer",
											color: "red",
											marginLeft: "10px",
											padding: "5px",
											fontSize: "1.5em",
										}}
									>
										<i className="fas fa-trash"></i>
									</button>
								</div>
							))}
						</div>
						<div style={{ marginTop: 10, fontWeight: "bold" }}>
							Total: {currencySymbol}
							{cart
								.reduce(
									(sum, ci) => sum + ci.price * ci.qty,
									0
								)
								.toFixed(2)}
						</div>
						<button
							className="btn btn-primary"
							onClick={handleBook}
							disabled={isBooking}
							style={{ marginTop: 15 }}
						>
							Confirm Order
						</button>
						{successMsg && (
							<div
								className="success-message"
								style={{ color: "green", marginTop: 10 }}
							>
								{successMsg}
							</div>
						)}{" "}
						{/* This might not be needed anymore or could show other success messages */}
						{error && !successMsg && (
							<div
								className="error-message"
								style={{ color: "red", marginTop: 10 }}
							>
								{error}
							</div>
						)}{" "}
						{/* Display error from handleBook if any */}
					</div>
				)}
			</div>
			<ImageModal item={modalItem} onClose={closeModal} />
		</div>
	);
}

export default TableBookingPage;
